const EPSILON_GAIN = 0.0001;
const MASTER_VOLUME = 0.55;
const MAX_DEFERRED_ACTIONS = 3;

export class SoundManager {
  constructor() {
    this.enabled = true;
    this.audioContext = null;
    this.masterGainNode = null;
    this.noiseBuffer = null;
    this.deferredActions = [];
    this.resumePromise = null;
  }

  async init() {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) {
      return false;
    }

    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (!this.audioContext || !this.masterGainNode || this.audioContext.state === "closed") {
        if (!this._createAudioContext(AudioContextConstructor)) {
          return false;
        }
      }

      const stateBeforeResume = this.audioContext.state;
      if (stateBeforeResume === "suspended" || stateBeforeResume === "interrupted") {
        try {
          await this.audioContext.resume();
        } catch {
          if (stateBeforeResume === "interrupted") {
            this._resetAudioContext();
            continue;
          }
          return false;
        }
      }

      if (this.audioContext.state === "running") {
        return true;
      }

      if (
        this.audioContext.state === "closed"
        || this.audioContext.state === "interrupted"
        || stateBeforeResume === "interrupted"
      ) {
        this._resetAudioContext();
        continue;
      }

      return false;
    }

    return false;
  }

  setEnabled(enabled) {
    this.enabled = Boolean(enabled);
    this._syncMasterGain();

    if (!this.enabled) {
      this.deferredActions.length = 0;
    }
  }

  isEnabled() {
    return this.enabled;
  }

  async _ensureAudioReady() {
    if (this.resumePromise) {
      return this.resumePromise;
    }

    this.resumePromise = (async () => {
      const ready = await this.init();
      if (!ready || !this.audioContext || this.audioContext.state !== "running" || !this.masterGainNode) {
        return false;
      }

      this._flushDeferredActions();
      return true;
    })();

    try {
      return await this.resumePromise;
    } finally {
      this.resumePromise = null;
    }
  }

  playCard() {
    const frequency = this._randomBetween(250, 400);
    this._playTone({
      type: Math.random() < 0.5 ? "triangle" : "square",
      frequency,
      duration: this._randomBetween(0.03, 0.08),
      volume: this._randomBetween(0.1, 0.15),
      endFrequency: frequency * this._randomBetween(0.96, 1.04)
    });
  }

  playInvalid() {
    const frequency = this._randomBetween(150, 220);
    this._playTone({
      type: "triangle",
      frequency,
      duration: this._randomBetween(0.05, 0.09),
      volume: this._randomBetween(0.07, 0.1),
      endFrequency: frequency * this._randomBetween(0.78, 0.9)
    });
  }

  playPickup() {
    const frequency = this._randomBetween(80, 120);
    this._playTone({
      type: "sine",
      frequency,
      duration: 0.18,
      volume: this._randomBetween(0.15, 0.2),
      endFrequency: frequency * this._randomBetween(0.68, 0.78)
    });
    this._playNoise({
      duration: 0.1,
      volume: this._randomBetween(0.05, 0.08),
      playbackRate: this._randomBetween(0.7, 0.95)
    });
  }

  playClear() {
    this._playNoise({
      duration: this._randomBetween(0.14, 0.22),
      volume: this._randomBetween(0.12, 0.18),
      playbackRate: this._randomBetween(0.55, 0.85)
    });
    this._playTone({
      type: "triangle",
      frequency: this._randomBetween(260, 320),
      duration: 0.09,
      volume: 0.08,
      endFrequency: this._randomBetween(190, 240)
    });
  }

  playReset() {
    const frequency = this._randomBetween(600, 900);
    this._playTone({
      type: "triangle",
      frequency,
      duration: 0.14,
      volume: this._randomBetween(0.07, 0.1),
      endFrequency: frequency * this._randomBetween(1.04, 1.1)
    });
  }

  playWin() {
    const startOffset = this._randomBetween(-12, 12);
    const sequence = [460, 580, 720];
    for (let index = 0; index < sequence.length; index += 1) {
      this._playTone({
        type: "triangle",
        frequency: sequence[index] + startOffset,
        duration: 0.12,
        volume: 0.1,
        startOffset: index * 0.1,
        endFrequency: sequence[index] + startOffset + 12
      });
    }
  }

  playLose() {
    const base = this._randomBetween(300, 360);
    this._playTone({
      type: "triangle",
      frequency: base,
      duration: 0.13,
      volume: 0.09,
      endFrequency: base * 0.82
    });
    this._playTone({
      type: "square",
      frequency: base * 0.72,
      duration: 0.18,
      volume: 0.08,
      startOffset: 0.1,
      endFrequency: base * 0.52
    });
  }

  _playTone(options) {
    this._withAudio((context) => {
      const duration = Math.max(0.02, options.duration ?? 0.08);
      const startTime = context.currentTime + (options.startOffset ?? 0);
      const endTime = startTime + duration;

      const oscillator = this._createOscillator(context, {
        type: options.type ?? "triangle",
        frequency: options.frequency ?? 300,
        startTime,
        endTime,
        endFrequency: options.endFrequency
      });

      const gainNode = context.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(this.masterGainNode);

      this._applyGainEnvelope(gainNode.gain, {
        startTime,
        duration,
        peak: this._clamp(options.volume ?? 0.1, 0.02, 0.35),
        attack: 0.003,
        release: Math.max(0.03, duration * 0.7)
      });

      oscillator.start(startTime);
      oscillator.stop(endTime + 0.02);
      oscillator.addEventListener(
        "ended",
        () => {
          oscillator.disconnect();
          gainNode.disconnect();
        },
        { once: true }
      );
    });
  }

  _playNoise(options) {
    this._withAudio((context) => {
      const duration = Math.max(0.02, options.duration ?? 0.1);
      const startTime = context.currentTime + (options.startOffset ?? 0);
      const endTime = startTime + duration;

      const source = context.createBufferSource();
      const noiseBuffer = this._getNoiseBuffer();
      if (!noiseBuffer) {
        return;
      }
      source.buffer = noiseBuffer;
      source.playbackRate.value = this._clamp(options.playbackRate ?? 1, 0.25, 2.5);

      const gainNode = context.createGain();
      source.connect(gainNode);
      gainNode.connect(this.masterGainNode);

      this._applyGainEnvelope(gainNode.gain, {
        startTime,
        duration,
        peak: this._clamp(options.volume ?? 0.08, 0.02, 0.3),
        attack: 0.002,
        release: Math.max(0.04, duration * 0.9)
      });

      source.start(startTime);
      source.stop(endTime + 0.02);
      source.addEventListener(
        "ended",
        () => {
          source.disconnect();
          gainNode.disconnect();
        },
        { once: true }
      );
    });
  }

  _createOscillator(context, options) {
    const oscillator = context.createOscillator();
    oscillator.type = options.type;
    oscillator.frequency.setValueAtTime(options.frequency, options.startTime);

    if (options.endFrequency != null) {
      oscillator.frequency.linearRampToValueAtTime(options.endFrequency, options.endTime);
    }

    return oscillator;
  }

  _applyGainEnvelope(gainParam, options) {
    const attack = Math.max(0.001, options.attack ?? 0.003);
    const release = Math.max(0.01, options.release ?? 0.05);
    const duration = Math.max(attack + release, options.duration ?? 0.08);
    const sustainTime = Math.max(0, duration - attack - release);

    const attackEnd = options.startTime + attack;
    const releaseStart = attackEnd + sustainTime;
    const envelopeEnd = releaseStart + release;

    gainParam.cancelScheduledValues(options.startTime);
    gainParam.setValueAtTime(EPSILON_GAIN, options.startTime);
    gainParam.linearRampToValueAtTime(options.peak, attackEnd);
    gainParam.setValueAtTime(options.peak, releaseStart);
    gainParam.exponentialRampToValueAtTime(EPSILON_GAIN, envelopeEnd);

    return envelopeEnd;
  }

  _getNoiseBuffer() {
    if (!this.audioContext) {
      return null;
    }

    const sampleRate = this.audioContext.sampleRate;
    if (this.noiseBuffer && this.noiseBuffer.sampleRate === sampleRate) {
      return this.noiseBuffer;
    }

    const length = sampleRate;
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const channel = buffer.getChannelData(0);
    for (let index = 0; index < length; index += 1) {
      channel[index] = Math.random() * 2 - 1;
    }

    this.noiseBuffer = buffer;
    return this.noiseBuffer;
  }

  _withAudio(action) {
    if (!this.enabled) {
      return;
    }

    if (this.audioContext && this.audioContext.state === "running" && this.masterGainNode) {
      action(this.audioContext);
      return;
    }

    this._queueDeferredAction(action);
    void this._ensureAudioReady();
  }

  _createAudioContext(AudioContextConstructor) {
    try {
      this.audioContext = new AudioContextConstructor();
      this.masterGainNode = this.audioContext.createGain();
      this._syncMasterGain();
      this.masterGainNode.connect(this.audioContext.destination);
      this.noiseBuffer = null;
      return true;
    } catch {
      this._resetAudioContext();
      return false;
    }
  }

  _resetAudioContext() {
    if (this.masterGainNode) {
      try {
        this.masterGainNode.disconnect();
      } catch {
        // Ignore cleanup failures.
      }
    }
    this.audioContext = null;
    this.masterGainNode = null;
    this.noiseBuffer = null;
  }

  _syncMasterGain() {
    if (!this.masterGainNode) {
      return;
    }
    this.masterGainNode.gain.value = this.enabled ? MASTER_VOLUME : 0;
  }

  _queueDeferredAction(action) {
    this.deferredActions.push(action);
    if (this.deferredActions.length > MAX_DEFERRED_ACTIONS) {
      this.deferredActions.shift();
    }
  }

  _flushDeferredActions() {
    if (!this.enabled || !this.audioContext || this.audioContext.state !== "running" || !this.masterGainNode) {
      return;
    }

    const pendingActions = this.deferredActions.splice(0);
    for (const pendingAction of pendingActions) {
      pendingAction(this.audioContext);
    }
  }

  _randomBetween(min, max) {
    return min + Math.random() * (max - min);
  }

  _clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }
}

export default SoundManager;
